/**
 * momentoBuckets.js — mapeo momento (texto libre) → subpestaña (bucket).
 *
 * Los `peticion.momento` son texto libre (guardados históricamente como
 * "Ceremonia", "Entrada Banquete", "Corte Tarta", "Hora Loca", ...). La UI de
 * subpestañas los agrupa en 4 buckets fijos: ceremonia / coctel / banquete /
 * baile. Este módulo centraliza el mapeo y las utilidades para agrupar,
 * contar y decidir qué subpestañas mostrar.
 *
 * Regla clave: NO cambiamos la estructura de datos existente. `peticion.momento`
 * sigue siendo el texto libre; sólo lo usamos como pista para clasificar.
 */

export const BUCKETS = ['ceremonia', 'coctel', 'banquete', 'baile'];

export const BUCKET_LABELS = {
  ceremonia: 'Ceremonia',
  coctel: 'Cóctel',
  banquete: 'Banquete',
  baile: 'Baile',
};

/** Valor que se asigna a `momento` cuando importas/asignas peticiones a una
 *  subpestaña concreta. Homogeneizado (2026-11-01): siempre el mismo texto
 *  canónico que `BUCKET_LABELS`, sin sufijos ("Baile", no "Baile General";
 *  "Banquete", no "Entrada Banquete") — el detalle específico va a `nota`. */
export const BUCKET_DEFAULT_MOMENTO = { ...BUCKET_LABELS };

/** Keywords (ya normalizados sin acentos y en minúsculas) por bucket, según
 *  el mapeo confirmado por el usuario en producción. */
export const BUCKET_KEYWORDS = {
  ceremonia: [
    'ceremonia', 'entrada novia', 'entrada novio', 'entrada novios', 'entrada',
    'votos', 'anillos', 'lectura', 'difuntos',
    'salida', 'salida ceremonia', 'salida novios',
    'postceremonia', 'preceremonia',
  ],
  coctel: [
    'entrada coctel', 'entrada a coctel', 'entrada al coctel', 'coctel', 'cocktail', 'aperitivo',
  ],
  banquete: [
    'entrada banquete', 'entrada a banquete', 'entrada al banquete',
    'entrada comedor', 'entrada a comedor', 'entrada al comedor',
    'entrada novios comedor', 'entrada novios banquete', 'entrada novios salon',
    'banquete', 'comedor',
    'tarta', 'corte tarta', 'corte de tarta',
    'regalo', 'regalo amigos', 'regalo amigas', 'regalo madres', 'regalo padres',
    'sois los siguientes', 'embarazadas', 'amigos', 'amigas',
    'padres', 'madres',
    'brindis', 'postre', 'sorbete', 'cena', 'comida',
    'primer plato', 'segundo plato', 'discurso',
  ],
  baile: [
    'baile nupcial', 'arreglo nupcial', 'primer baile', 'primera baile',
    'baile', 'baile general',
    'hora loca', 'cierre', 'verbena', 'ramo',
  ],
};

/** Normaliza texto (minúsculas + sin acentos + trim + colapsa espacios). */
export const normalizeMomento = (s) => {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Dado un texto de momento (posiblemente vacío), devuelve el bucket id o null. */
export const getBucketForMomento = (momento) => {
  const norm = normalizeMomento(momento);
  if (!norm) return null;
  // Búsqueda por keywords (con límite de palabra \b, para que "cena" no
  // case dentro de "escena"/"escenario"): prioriza la coincidencia más larga
  // (evita que "entrada" en Ceremonia gane a "entrada banquete").
  let best = null;
  let bestLen = 0;
  for (const [bucket, kws] of Object.entries(BUCKET_KEYWORDS)) {
    for (const kw of kws) {
      const re = new RegExp(`\\b${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
      if (re.test(norm) && kw.length > bestLen) {
        best = bucket;
        bestLen = kw.length;
      }
    }
  }
  // "Entrada" ambiguo (ceremonia vs banquete): si el texto menciona
  // "entrada" y también una palabra específica de banquete (comedor,
  // banquete, salón...), prevalece Banquete sobre el match genérico de
  // "entrada"/"entrada novios" en Ceremonia.
  if (best === 'ceremonia' && /\bentrada\b/.test(norm)) {
    for (const kw of ['comedor', 'banquete', 'salon']) {
      if (new RegExp(`\\b${kw}\\b`).test(norm)) return 'banquete';
    }
  }
  return best;
};

/** Valores de `momento` que son placeholders genéricos (asignados por defecto
 *  al mover/clasificar una petición a un bucket), sin información real sobre
 *  el momento concreto de la canción. Comparación insensible a mayúsculas y
 *  acentos (ej. "BAILE GENERAL", "baile general" y "Baile General" cuentan
 *  todos como el mismo placeholder genérico). */
const GENERIC_MOMENTOS_NORM = new Set(
  Object.values(BUCKET_DEFAULT_MOMENTO).map(normalizeMomento)
);

/** Etiqueta a mostrar para una petición, unificando `momento` (clasificación
 *  del evento, normalmente la más específica cuando viene de IA/documentos)
 *  y `nota` (texto libre del DJ). Si `momento` es un placeholder genérico
 *  (ej. "Baile General" tras clasificar sin detalle) y hay una `nota` más
 *  concreta (ej. "Hora loca"), se prioriza la nota. En cualquier otro caso
 *  se mantiene `momento` como principal (evita tapar un momento real con una
 *  nota poco útil, ej. "Detectada del documento…"). */
export const getDisplayLabel = (peticion) => {
  if (!peticion) return '';
  const momento = String(peticion.momento || '').trim();
  const nota = String(peticion.nota || '').trim();
  if (momento && GENERIC_MOMENTOS_NORM.has(normalizeMomento(momento)) && nota) return nota;
  return momento || nota;
};

/** Adivina el bucket de una petición combinando `momento` + `nota` +
 *  `origen_documento`. Si no hay pistas, devuelve 'baile' (default operativo). */
export const guessBucketFromPeticion = (peticion) => {
  if (!peticion || typeof peticion !== 'object') return 'baile';
  const direct = getBucketForMomento(peticion.momento);
  if (direct) return direct;
  // Pistas secundarias: nota + origen_documento suelen contener "entrada", "tarta"...
  const hint =
    (peticion.nota || '') + ' ' +
    (peticion.origen_documento || '') + ' ' +
    (peticion.momento_hint || '');
  const fromHint = getBucketForMomento(hint);
  if (fromHint) return fromHint;
  // Default operativo: Baile (es donde se acumulan la mayoría de peticiones).
  return 'baile';
};

/** Cuenta peticiones por bucket. */
export const countByBucket = (peticiones = []) => {
  const counts = { ceremonia: 0, coctel: 0, banquete: 0, baile: 0 };
  for (const p of peticiones || []) {
    const b = guessBucketFromPeticion(p);
    if (b in counts) counts[b] += 1;
  }
  return counts;
};

/** Agrupa peticiones por bucket. */
export const groupByBucket = (peticiones = []) => {
  const groups = { ceremonia: [], coctel: [], banquete: [], baile: [] };
  for (const p of peticiones || []) {
    const b = guessBucketFromPeticion(p);
    if (b in groups) groups[b].push(p);
  }
  return groups;
};

/**
 * Devuelve la lista de buckets a mostrar como subpestañas, según:
 *  - counts (auto-ocultar bucket con 0 peticiones)
 *  - overrides (mapa {bucket: bool}). true = forzar mostrar, false = forzar ocultar.
 *  - Baile SIEMPRE se muestra (fallback + destino por defecto).
 */
export const getVisibleBuckets = (counts = {}, overrides = {}) => {
  const visible = [];
  for (const b of BUCKETS) {
    if (b === 'baile') {
      visible.push(b);
      continue;
    }
    const ov = overrides ? overrides[b] : undefined;
    if (ov === true) {
      visible.push(b);
    } else if (ov === false) {
      // Forzar ocultar
      continue;
    } else if ((counts[b] || 0) > 0) {
      visible.push(b);
    }
  }
  return visible;
};
